import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, unref as _unref, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createBlock as _createBlock, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, mergeProps as _mergeProps, withModifiers as _withModifiers, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "table-bordered-centered"
}
const _hoisted_2 = {
  key: 1,
  class: "mt-4"
}
const _hoisted_3 = { style: {"width":"100%","display":"flex","justify-content":"space-between","align-items":"center"} }
const _hoisted_4 = { class: "text-truncate" }
const _hoisted_5 = { class: "asn-tree" }
const _hoisted_6 = {
  key: 1,
  class: "text-h6 text-center pa-5"
}
const _hoisted_7 = { class: "copy-anchor" }
const _hoisted_8 = {
  key: 0,
  class: "byte-grid"
}
const _hoisted_9 = ["data-node-id", "onClick"]

import { ref, computed, watch, onMounted, onBeforeUnmount, onUpdated, nextTick } from 'vue'
import { useDisplay } from 'vuetify'
import { useTabsStore } from '@/stores/tabs'
import axios from 'axios'

import TreeNodeComponent from '@/components/TreeNode.vue'
import UploadCard from '@/components/UploadCard.vue'
import ElementMenu from '@/components/ElementMenu.vue'
import MenuComponent from '@/components/MenuComponent.vue'
import KeyLoader from '@/components/KeyLoader.vue'

import type { TreeNode, CureReport, ContextMenuItem } from '@/types/editor'

// ─── Setup ────────────────────────────────────────────────────────────────────

const PUBLIC_BACKEND = 'https://api.asn1.app/'
const LOCAL_BACKEND = 'http://localhost:21999/'

// ─── State ────────────────────────────────────────────────────────────────────

type ExportFormat = (typeof formats)[number]

interface FlatByte {
  value: number
  type: 'tag' | 'length' | 'content'
  nodeId: number
}


export default /*@__PURE__*/_defineComponent({
  __name: 'EditorView',
  setup(__props) {

const { mobile: isMobile } = useDisplay()
const store = useTabsStore()

const menuX = ref(0)
const menuY = ref(0)
const parent = ref(0)
const activeNode = ref<TreeNode | null>(null)
const showElementMenu = ref(false)
const bytesTop = ref(0)
const backendUrl = ref(LOCAL_BACKEND)
const reachable = ref(false)
const load = ref(false)
const loading = ref(false)
const reports = ref<CureReport[]>([])
const showReports = ref(false)
const reportTab = ref(0)
const simplify = ref(false)
const showShareDialog = ref(false)
const shareUrl = ref('')
const shareUrlTooLong = ref(false)
const copied = ref(false)
const showKeyLoader = ref(false)

const activatorRef = ref<HTMLDivElement | null>(null)
const bytesRef = ref<HTMLDivElement | null>(null)

const formats = ['binary', 'base64', 'json', 'repository'] as const
const rpkiTypes = ['roa', 'mft', 'crl', 'cer', 'asa', 'gbr']

// ─── Context menu ─────────────────────────────────────────────────────────────

const contextItems = computed<ContextMenuItem[]>(() => [
  {
    title: 'COPY ...',
    action: () => {},
    children: [
      { title: 'NODE', action: () => copy('node') },
      { title: 'CONTENT', action: () => copy('content') },
      { title: 'AS BASE64', action: () => copy('base64') },
      { title: 'AS HEX', action: () => copy('hex') }
    ]
  },
  { title: 'DUPLICATE NODE', action: () => duplicateNode(activeNode.value) },
  { title: 'DELETE NODE', action: () => deleteNode() },
  { title: 'EDIT NODE', action: () => { showElementMenu.value = true } },
  {
    title: 'ADD CHILD',
    action: () => {
      parent.value = activeNode.value?.id ?? 0
      activeNode.value = null
      showElementMenu.value = true
    }
  }
])

// ─── Computed ─────────────────────────────────────────────────────────────────

const objectType = computed(() => store.state?.infer_object_type() ?? '')

const currentTab = computed({
  get: () => store.currentTab,
  set: (value: string | null) => {
    if (value) store.tabSelected(value)
  }
})

const flatBytes = computed<FlatByte[]>(() => {
  const bytes: FlatByte[] = []
  const root = findRoot()
  if (!root || root.id === -1) return bytes

  const traverse = (node: TreeNode) => {
    if (node.tag?.[2]) {
      for (const b of node.tag[2]) bytes.push({ value: b, type: 'tag', nodeId: node.id })
    }
    if (node.length?.[2]) {
      for (const b of node.length[2]) bytes.push({ value: b, type: 'length', nodeId: node.id })
    }
    if (node.content?.[3]) {
      for (const b of node.content[3]) bytes.push({ value: b, type: 'content', nodeId: node.id })
    }
    if (node.children) {
      for (const childId of node.children) {
        const childNode = store.getNodeFromId(childId)
        if (childNode) traverse(childNode)
      }
    }
  }

  traverse(root)
  return bytes
})

const highlightedNodeAndDescendants = computed<Set<number>>(() => {
  const highlightedId = store.highlighted
  const highlightSet = new Set<number>()

  if (highlightedId === null || highlightedId === undefined || highlightedId === -1) {
    return highlightSet
  }

  const collectChildren = (nodeId: number) => {
    if (highlightSet.has(nodeId)) return
    highlightSet.add(nodeId)
    const node = store.getNodeFromId(nodeId)
    if (node?.children) {
      for (const childId of node.children) collectChildren(childId)
    }
  }

  collectChildren(highlightedId)
  return highlightSet
})

// ─── Watchers ─────────────────────────────────────────────────────────────────

watch(
  () => store.highlighted,
  (id) => {
    if (id === null || id === undefined || id === -1) return

    activeNode.value = store.getNodeFromId(id)

    if (!bytesRef.value) return
    const byteContainer = bytesRef.value
    const targetElement = byteContainer.querySelector(`span[data-node-id="${id}"]`) as HTMLElement | null

    if (targetElement) {
      const containerHeight = byteContainer.clientHeight
      const targetTopRelativeToContainer = targetElement.offsetTop
      const scrollTop =
        targetTopRelativeToContainer - containerHeight / 2 + targetElement.clientHeight / 2

      byteContainer.scrollTo({ top: scrollTop, behavior: 'smooth' })
    }
  }
)

// ─── Methods ──────────────────────────────────────────────────────────────────

function uint8ToBase64(uint8Array: Uint8Array | number[]): string {
  const base64Chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/'
  let result = ''

  for (let i = 0; i < uint8Array.length; i += 3) {
    const byte1 = uint8Array[i]
    const byte2 = i + 1 < uint8Array.length ? uint8Array[i + 1] : 0
    const byte3 = i + 2 < uint8Array.length ? uint8Array[i + 2] : 0
    const triplet = (byte1 << 16) | (byte2 << 8) | byte3

    result += base64Chars[(triplet >> 18) & 0x3f]
    result += base64Chars[(triplet >> 12) & 0x3f]
    result += i + 1 < uint8Array.length ? base64Chars[(triplet >> 6) & 0x3f] : '='
    result += i + 2 < uint8Array.length ? base64Chars[triplet & 0x3f] : '='
  }

  return result
}

function generateShareUrl(): string {
  if (!store.state) return ''
  try {
    const base64Data = store.state.export_base64()
    const urlSafeBase64 = base64Data
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '')

    const baseUrl = window.location.origin + window.location.pathname
    const name = encodeURIComponent(store.name || 'Shared')
    return `${baseUrl}?data=${urlSafeBase64}&name=${name}`
  } catch (e) {
    console.error('Error generating share URL:', e)
    return ''
  }
}

function openShareDialog(): void {
  shareUrl.value = generateShareUrl()
  shareUrlTooLong.value = shareUrl.value.length > 2000
  copied.value = false
  showShareDialog.value = true
}

async function copyShareUrl(): Promise<void> {
  try {
    await navigator.clipboard.writeText(shareUrl.value)
    copied.value = true
    setTimeout(() => {
      copied.value = false
    }, 3000)
  } catch (e) {
    console.error('Failed to copy URL:', e)
  }
}

function loadFromUrl(): boolean {
  const urlParams = new URLSearchParams(window.location.search)
  const data = urlParams.get('data')
  const name = urlParams.get('name')

  if (data) {
    try {
      let base64Data = data.replace(/-/g, '+').replace(/_/g, '/')
      const padding = base64Data.length % 4
      if (padding) base64Data += '='.repeat(4 - padding)

      const tabName = name ? decodeURIComponent(name) : 'Shared Object'
      store.addTab(tabName)

      store.stateSet({
        tab: store.currentTab,
        data: base64Data,
        type: 'base64'
      })

      window.history.replaceState({}, document.title, window.location.pathname)
      return true
    } catch (e: any) {
      console.error('Error loading data from URL:', e)
      alert('Failed to load the shared object: ' + (e.message ?? 'Unknown error'))
    }
  }
  return false
}

async function runTestCase(): Promise<void> {
  const z = store.state!.repositorify()
  const serialized = uint8ToBase64(z)

  try {
    const response = await axios.post(backendUrl.value + 'execute', serialized, {
      headers: { 'Content-Type': 'text/plain' }
    })
    showReports.value = true

    const report: CureReport = {
      name: store.name,
      state: store.state!.encode_store(),
      report: response.data.map(JSON.parse)
    }
    reports.value.push(report)
    reportTab.value = reports.value.length - 1
  } catch (error) {
    console.error('Error during test case execution:', error)
    alert('Error during test case execution. Please check the console for details.')
  }
}

function openMenu(x: number, y: number, id: number): void {
  menuX.value = x
  menuY.value = y
  activatorRef.value?.click()
  store.elementHighlighted(id)
  activeNode.value = store.getNodeFromId(id)
}

function dec2hex(i: number): string {
  return (i + 0x10000).toString(16).substr(-2).toUpperCase()
}

function copy(type: 'node' | 'content' | 'base64' | 'hex'): void {
  if (!activeNode.value) return

  if (type === 'node') {
    store.copiedCellSet(activeNode.value)
  } else if (type === 'hex') {
    const array = [
      ...activeNode.value.tag[2],
      ...activeNode.value.length[2],
      ...activeNode.value.content[3]
    ]
    navigator.clipboard.writeText(array.map(dec2hex).join(' '))
  } else if (type === 'base64') {
    const array = [
      ...activeNode.value.tag[2],
      ...activeNode.value.length[2],
      ...activeNode.value.content[3]
    ]
    navigator.clipboard.writeText(uint8ToBase64(array))
  } else if (type === 'content') {
    navigator.clipboard.writeText(activeNode.value.content[1])
  }
}

function duplicateNode(node: TreeNode | null): void {
  if (!node) return

  store.nodeAdded({
    tab: store.currentTab,
    tag: node.tag[0],
    content: node.content[2],
    parent: node.parent,
    label: node.label,
    index: store.getNodeFromId(node.parent).children.indexOf(node.id) + 1
  })
}

function deleteNode(): void {
  if (!activeNode.value) return
  if (confirm('Are you sure you want to delete this node?')) {
    store.nodeRemoved({
      id: activeNode.value.id,
      tab: store.currentTab
    })
  }
}

function findRoot(): TreeNode {
  const candidates = store.tree.filter(node => node.id === node.parent)
  return candidates.length > 0 ? candidates[0] : ({ children: [], id: -1 } as any)
}

function download(format: ExportFormat): void {
  let content: Uint8Array | string | null = null
  let fileName = store.name
  let type = ''

  loading.value = true
  switch (format) {
    case 'binary':
      content = store.state!.export_bin()
      fileName += '.bin'
      type = 'application/octet-stream'
      break
    case 'base64':
      content = store.state!.export_base64()
      fileName += '.txt'
      type = 'text/plain'
      break
    case 'json':
      content = store.state!.encode_store()
      fileName += '.json'
      type = 'application/json'
      break
    case 'repository':
      content = store.state!.repositorify()
      fileName += '.tar.gz'
      type = 'application/x-gzip'
      break
  }

  const blob = new Blob([content as any], { type })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = fileName

  document.body.appendChild(link)
  link.click()

  loading.value = false
  document.body.removeChild(link)
  URL.revokeObjectURL(link.href)
}

function downloadReport(report: CureReport): void {
  const exportData = {
    version: '1.0',
    exportedAt: new Date().toISOString(),
    name: report.name,
    state: report.state,
    report: report.report
  }

  const content = JSON.stringify(exportData, null, 2)
  const fileName = `${report.name.replace(/[^a-zA-Z0-9_-]/g, '_')}_report.json`
  const blob = new Blob([content], { type: 'application/json' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = fileName

  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(link.href)
}

function loadTestCase(name: string, storeData: string): void {
  store.addTab(name)
  store.stateSet({
    tab: store.currentTab,
    data: storeData,
    type: 'json'
  })
}

function handleReportLoaded(reportData: { name: string; state: string; report: any[] }): void {
  reports.value.push({
    name: reportData.name,
    state: reportData.state,
    report: reportData.report
  })
  reportTab.value = reports.value.length - 1
}

function handleKeydown(event: KeyboardEvent): void {
  if (event.ctrlKey && event.key === 'z') store.undo()
  if (event.ctrlKey && event.key === 'y') store.redo()
}

async function copyDER(): Promise<void> {
  if (flatBytes.value.length === 0) return
  const hex = flatBytes.value.map(b => dec2hex(b.value)).join(' ')
  await navigator.clipboard.writeText(hex)
}

// ─── Lifecycle ────────────────────────────────────────────────────────────────

// Check backend reachability before mount
async function probeBackend(): Promise<void> {
  try {
    await axios.get(LOCAL_BACKEND + 'probe')
    reachable.value = true
  } catch {
    console.log('No local backend detected at', LOCAL_BACKEND)
    try {
      await axios.get(PUBLIC_BACKEND + 'probe')
      backendUrl.value = PUBLIC_BACKEND
      reachable.value = true
    } catch {
      console.error('Backend not reachable at', backendUrl.value)
      reachable.value = false
    }
  }
}

onMounted(() => {
  document.title = 'Live ASN.1 Editor & Parser | DERP'
  window.addEventListener('keydown', handleKeydown)
  probeBackend()

  nextTick(() => {
    const tryLoad = (attempts = 0) => {
      try {
        loadFromUrl()
      } catch (e) {
        if (attempts < 5) {
          console.log('WASM may not be ready, retrying...', attempts + 1)
          setTimeout(() => tryLoad(attempts + 1), 200)
        } else {
          console.error('Failed to load from URL after retries:', e)
        }
      }
    }
    setTimeout(() => tryLoad(), 100)
  })
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeydown)
})

onUpdated(() => {
  if (bytesRef.value) {
    const containerRect = bytesRef.value.getBoundingClientRect()
    bytesTop.value = containerRect.top
  }
})

return (_ctx: any,_cache: any) => {
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!
  const _component_v_overlay = _resolveComponent("v-overlay")!
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_tab = _resolveComponent("v-tab")!
  const _component_v_tabs = _resolveComponent("v-tabs")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_window_item = _resolveComponent("v-window-item")!
  const _component_v_window = _resolveComponent("v-window")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_divider = _resolveComponent("v-divider")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_alert = _resolveComponent("v-alert")!
  const _component_v_dialog = _resolveComponent("v-dialog")!
  const _component_v_checkbox = _resolveComponent("v-checkbox")!
  const _component_v_menu = _resolveComponent("v-menu")!
  const _component_v_container = _resolveComponent("v-container")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_component_v_overlay, {
      modelValue: loading.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((loading).value = $event))
    }, {
      default: _withCtx(() => [
        _createVNode(_component_v_progress_circular, {
          indeterminate: "",
          color: "primary"
        })
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(_component_v_overlay, {
      modelValue: load.value,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((load).value = $event))
    }, {
      default: _withCtx(() => [
        _createVNode(UploadCard, {
          onUpload: _cache[1] || (_cache[1] = ($event: any) => (load.value = false)),
          onReportLoaded: handleReportLoaded
        })
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(_component_v_overlay, {
      modelValue: showElementMenu.value,
      "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((showElementMenu).value = $event))
    }, {
      default: _withCtx(() => [
        _createVNode(ElementMenu, {
          parent: parent.value,
          node: activeNode.value,
          onClose: _cache[3] || (_cache[3] = ($event: any) => (showElementMenu.value = false))
        }, null, 8, ["parent", "node"])
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(_component_v_overlay, {
      modelValue: showKeyLoader.value,
      "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((showKeyLoader).value = $event))
    }, {
      default: _withCtx(() => [
        _createVNode(KeyLoader, {
          activeNode: activeNode.value,
          onClose: _cache[5] || (_cache[5] = ($event: any) => (showKeyLoader.value = false))
        }, null, 8, ["activeNode"])
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(_component_v_overlay, {
      modelValue: showReports.value,
      "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((showReports).value = $event))
    }, {
      default: _withCtx(() => [
        _createVNode(_component_v_card, {
          width: _unref(isMobile) ? '95vw' : '70vw',
          height: _unref(isMobile) ? '90vh' : '70vh',
          class: "d-flex flex-column"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_v_card_title, { class: "text-center bg-primary" }, {
              default: _withCtx(() => [...(_cache[26] || (_cache[26] = [
                _createTextVNode("REPORTS", -1)
              ]))]),
              _: 1
            }),
            _createVNode(_component_v_card_text, { class: "flex-grow-1 overflow-y-auto" }, {
              default: _withCtx(() => [
                _createVNode(_component_v_row, {
                  "no-gutters": "",
                  class: "h-100"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_col, {
                      cols: "12",
                      md: "2"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_tabs, {
                          modelValue: reportTab.value,
                          "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((reportTab).value = $event)),
                          direction: _unref(isMobile) ? 'horizontal' : 'vertical',
                          class: "h-100",
                          "show-arrows": ""
                        }, {
                          default: _withCtx(() => [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(reports.value, (report, idx) => {
                              return (_openBlock(), _createBlock(_component_v_tab, {
                                key: `tab-${idx}`,
                                value: idx,
                                class: "text-capitalize"
                              }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(report.name.replace(/_/g, ' ')), 1)
                                ]),
                                _: 2
                              }, 1032, ["value"]))
                            }), 128))
                          ]),
                          _: 1
                        }, 8, ["modelValue", "direction"])
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_v_col, {
                      cols: "12",
                      md: "10"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_window, {
                          modelValue: reportTab.value,
                          "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((reportTab).value = $event)),
                          class: "h-100"
                        }, {
                          default: _withCtx(() => [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(reports.value, (report, idx) => {
                              return (_openBlock(), _createBlock(_component_v_window_item, {
                                key: `window-${idx}`,
                                value: idx,
                                class: "pa-4"
                              }, {
                                default: _withCtx(() => [
                                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList([...report.report].sort(
                    (a, b) => Number(b.crashed) - Number(a.crashed)
                  ), (rp, i) => {
                                    return (_openBlock(), _createElementBlock("div", { key: i }, [
                                      _createElementVNode("h3", null, _toDisplayString(rp.name), 1),
                                      _cache[33] || (_cache[33] = _createElementVNode("br", null, null, -1)),
                                      _createElementVNode("span", {
                                        class: _normalizeClass({ crashed: rp.crashed })
                                      }, _toDisplayString(rp.crashed ? 'Crash: True' : 'Crash: False'), 3),
                                      _cache[34] || (_cache[34] = _createElementVNode("br", null, null, -1)),
                                      (!rp.crashed && !_unref(isMobile))
                                        ? (_openBlock(), _createElementBlock("table", _hoisted_1, [
                                            _cache[28] || (_cache[28] = _createElementVNode("thead", null, [
                                              _createElementVNode("tr", null, [
                                                _createElementVNode("th", null, "ASN"),
                                                _createElementVNode("th", null, "IP Prefix"),
                                                _createElementVNode("th", null, "Max Length"),
                                                _createElementVNode("th", null, "Trust Anchor")
                                              ])
                                            ], -1)),
                                            _createElementVNode("tbody", null, [
                                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(rp.vrps.content, (vrp, i3) => {
                                                return (_openBlock(), _createElementBlock("tr", { key: i3 }, [
                                                  _createElementVNode("td", null, _toDisplayString(vrp.asn), 1),
                                                  _createElementVNode("td", null, _toDisplayString(vrp.ip.ip_s), 1),
                                                  _createElementVNode("td", null, _toDisplayString(vrp.ip.max_len), 1),
                                                  _cache[27] || (_cache[27] = _createElementVNode("td", null, "ta", -1))
                                                ]))
                                              }), 128))
                                            ])
                                          ]))
                                        : _createCommentVNode("", true),
                                      (!rp.crashed && _unref(isMobile))
                                        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(rp.vrps.content, (vrp, i3) => {
                                              return (_openBlock(), _createBlock(_component_v_card, {
                                                key: i3,
                                                class: "mb-3",
                                                variant: "outlined"
                                              }, {
                                                default: _withCtx(() => [
                                                  _createVNode(_component_v_card_text, null, {
                                                    default: _withCtx(() => [
                                                      _createElementVNode("div", null, [
                                                        _cache[29] || (_cache[29] = _createElementVNode("strong", null, "ASN:", -1)),
                                                        _createTextVNode(" " + _toDisplayString(vrp.asn), 1)
                                                      ]),
                                                      _createElementVNode("div", null, [
                                                        _cache[30] || (_cache[30] = _createElementVNode("strong", null, "IP Prefix:", -1)),
                                                        _createTextVNode(" " + _toDisplayString(vrp.ip.ip_s), 1)
                                                      ]),
                                                      _createElementVNode("div", null, [
                                                        _cache[31] || (_cache[31] = _createElementVNode("strong", null, "Max Length:", -1)),
                                                        _createTextVNode(" " + _toDisplayString(vrp.ip.max_len), 1)
                                                      ]),
                                                      _cache[32] || (_cache[32] = _createElementVNode("div", null, [
                                                        _createElementVNode("strong", null, "Trust Anchor:"),
                                                        _createTextVNode(" ta")
                                                      ], -1))
                                                    ]),
                                                    _: 2
                                                  }, 1024)
                                                ]),
                                                _: 2
                                              }, 1024))
                                            }), 128))
                                          ]))
                                        : _createCommentVNode("", true),
                                      _createElementVNode("span", {
                                        class: _normalizeClass({ crashed: rp.crashed })
                                      }, _toDisplayString(rp.crashed ? 'Errors:' : 'Logs:'), 3),
                                      _cache[35] || (_cache[35] = _createElementVNode("br", null, null, -1)),
                                      _createElementVNode("pre", {
                                        class: _normalizeClass(["text-pre-wrap", { 'crashed-thin': rp.crashed }])
                                      }, _toDisplayString(rp.errors), 3)
                                    ]))
                                  }), 128))
                                ]),
                                _: 2
                              }, 1032, ["value"]))
                            }), 128))
                          ]),
                          _: 1
                        }, 8, ["modelValue"])
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createVNode(_component_v_divider),
            _createVNode(_component_v_card_actions, { class: "pa-4" }, {
              default: _withCtx(() => [
                _createVNode(_component_v_spacer),
                _createVNode(_component_v_btn, {
                  onClick: _cache[9] || (_cache[9] = ($event: any) => (downloadReport(reports.value[reportTab.value]))),
                  color: "secondary",
                  variant: "tonal",
                  size: _unref(isMobile) ? 'small' : 'default'
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_icon, { start: "" }, {
                      default: _withCtx(() => [...(_cache[36] || (_cache[36] = [
                        _createTextVNode("mdi-download", -1)
                      ]))]),
                      _: 1
                    }),
                    _cache[37] || (_cache[37] = _createTextVNode(" Download Report ", -1))
                  ]),
                  _: 1
                }, 8, ["size"]),
                _createVNode(_component_v_btn, {
                  onClick: _cache[10] || (_cache[10] = ($event: any) => (loadTestCase(reports.value[reportTab.value].name, reports.value[reportTab.value].state))),
                  color: "primary",
                  variant: "tonal",
                  size: _unref(isMobile) ? 'small' : 'default'
                }, {
                  default: _withCtx(() => [...(_cache[38] || (_cache[38] = [
                    _createTextVNode(" Load Test Case ", -1)
                  ]))]),
                  _: 1
                }, 8, ["size"]),
                _createVNode(_component_v_btn, {
                  onClick: _cache[11] || (_cache[11] = ($event: any) => (showReports.value = false)),
                  color: "grey",
                  variant: "tonal",
                  size: _unref(isMobile) ? 'small' : 'default'
                }, {
                  default: _withCtx(() => [...(_cache[39] || (_cache[39] = [
                    _createTextVNode(" Close ", -1)
                  ]))]),
                  _: 1
                }, 8, ["size"])
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["width", "height"])
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(_component_v_dialog, {
      modelValue: showShareDialog.value,
      "onUpdate:modelValue": _cache[16] || (_cache[16] = ($event: any) => ((showShareDialog).value = $event)),
      "max-width": "600"
    }, {
      default: _withCtx(() => [
        _createVNode(_component_v_card, null, {
          default: _withCtx(() => [
            _createVNode(_component_v_card_title, { class: "d-flex align-center" }, {
              default: _withCtx(() => [
                _createVNode(_component_v_icon, { start: "" }, {
                  default: _withCtx(() => [...(_cache[40] || (_cache[40] = [
                    _createTextVNode("mdi-share-variant", -1)
                  ]))]),
                  _: 1
                }),
                _cache[41] || (_cache[41] = _createTextVNode(" Share Link ", -1))
              ]),
              _: 1
            }),
            _createVNode(_component_v_card_text, null, {
              default: _withCtx(() => [
                _cache[44] || (_cache[44] = _createElementVNode("p", { class: "text-body-2 mb-3" }, " Copy this link to share the current ASN.1 object: ", -1)),
                _createVNode(_component_v_text_field, {
                  modelValue: shareUrl.value,
                  "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((shareUrl).value = $event)),
                  readonly: "",
                  variant: "outlined",
                  density: "compact",
                  "hide-details": "",
                  onFocus: _cache[14] || (_cache[14] = ($event: any) => (($event.target as HTMLInputElement)?.select()))
                }, {
                  "append-inner": _withCtx(() => [
                    _createVNode(_component_v_btn, {
                      icon: "mdi-content-copy",
                      size: "small",
                      variant: "text",
                      onClick: copyShareUrl
                    })
                  ]),
                  _: 1
                }, 8, ["modelValue"]),
                (shareUrlTooLong.value)
                  ? (_openBlock(), _createBlock(_component_v_alert, {
                      key: 0,
                      type: "warning",
                      variant: "tonal",
                      density: "compact",
                      class: "mt-3"
                    }, {
                      default: _withCtx(() => [...(_cache[42] || (_cache[42] = [
                        _createTextVNode(" The object is large, so the URL is quite long. Some browsers may not support URLs of this length. ", -1)
                      ]))]),
                      _: 1
                    }))
                  : _createCommentVNode("", true),
                (copied.value)
                  ? (_openBlock(), _createBlock(_component_v_alert, {
                      key: 1,
                      type: "success",
                      variant: "tonal",
                      density: "compact",
                      class: "mt-3"
                    }, {
                      default: _withCtx(() => [...(_cache[43] || (_cache[43] = [
                        _createTextVNode(" Link copied to clipboard! ", -1)
                      ]))]),
                      _: 1
                    }))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            }),
            _createVNode(_component_v_card_actions, null, {
              default: _withCtx(() => [
                _createVNode(_component_v_spacer),
                _createVNode(_component_v_btn, {
                  variant: "tonal",
                  onClick: _cache[15] || (_cache[15] = ($event: any) => (showShareDialog.value = false))
                }, {
                  default: _withCtx(() => [...(_cache[45] || (_cache[45] = [
                    _createTextVNode("Close", -1)
                  ]))]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(_component_v_container, { fluid: "" }, {
      default: _withCtx(() => [
        (_unref(store).tabs.length > 0)
          ? (_openBlock(), _createBlock(_component_v_row, { key: 0 }, {
              default: _withCtx(() => [
                _createVNode(_component_v_col, {
                  cols: "12",
                  sm: "auto"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_btn, {
                      onClick: _cache[17] || (_cache[17] = ($event: any) => (load.value = true)),
                      color: "primary",
                      block: _unref(isMobile)
                    }, {
                      default: _withCtx(() => [...(_cache[46] || (_cache[46] = [
                        _createTextVNode("IMPORT", -1)
                      ]))]),
                      _: 1
                    }, 8, ["block"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_v_col, {
                  cols: "12",
                  sm: "auto"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_btn, {
                      color: "primary",
                      block: _unref(isMobile)
                    }, {
                      default: _withCtx(() => [
                        _cache[47] || (_cache[47] = _createTextVNode(" EXPORT ", -1)),
                        _createVNode(MenuComponent, {
                          items: formats.map(f => ({ title: f.toUpperCase(), action: () => download(f) }))
                        }, null, 8, ["items"])
                      ]),
                      _: 1
                    }, 8, ["block"])
                  ]),
                  _: 1
                }),
                (_unref(store).tree.length > 0)
                  ? (_openBlock(), _createBlock(_component_v_col, {
                      key: 0,
                      cols: "12",
                      sm: "auto"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_btn, {
                          color: "primary",
                          onClick: openShareDialog,
                          block: _unref(isMobile)
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_icon, { start: "" }, {
                              default: _withCtx(() => [...(_cache[48] || (_cache[48] = [
                                _createTextVNode("mdi-share-variant", -1)
                              ]))]),
                              _: 1
                            }),
                            _cache[49] || (_cache[49] = _createTextVNode(" SHARE ", -1))
                          ]),
                          _: 1
                        }, 8, ["block"])
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true),
                (_unref(store).tree.length > 0)
                  ? (_openBlock(), _createBlock(_component_v_col, {
                      key: 1,
                      cols: "12",
                      sm: "auto"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_btn, {
                          color: "primary",
                          onClick: _cache[18] || (_cache[18] = ($event: any) => (showKeyLoader.value = true)),
                          block: _unref(isMobile)
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_icon, { start: "" }, {
                              default: _withCtx(() => [...(_cache[50] || (_cache[50] = [
                                _createTextVNode("mdi-key-variant", -1)
                              ]))]),
                              _: 1
                            }),
                            _cache[51] || (_cache[51] = _createTextVNode(" KEY LOADER ", -1))
                          ]),
                          _: 1
                        }, 8, ["block"])
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true),
                (_unref(store).tree.length > 0 && reachable.value && rpkiTypes.includes(objectType.value))
                  ? (_openBlock(), _createBlock(_component_v_col, {
                      key: 2,
                      cols: "12",
                      sm: "auto"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_btn, {
                          color: "primary",
                          onClick: runTestCase,
                          block: _unref(isMobile)
                        }, {
                          default: _withCtx(() => [...(_cache[52] || (_cache[52] = [
                            _createTextVNode(" RUN TEST CASE WITH CURE ", -1)
                          ]))]),
                          _: 1
                        }, 8, ["block"])
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true),
                (reports.value.length > 0)
                  ? (_openBlock(), _createBlock(_component_v_col, {
                      key: 3,
                      cols: "12",
                      sm: "auto"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_btn, {
                          color: "primary",
                          onClick: _cache[19] || (_cache[19] = ($event: any) => (showReports.value = true)),
                          block: _unref(isMobile)
                        }, {
                          default: _withCtx(() => [...(_cache[53] || (_cache[53] = [
                            _createTextVNode("SHOW REPORTS", -1)
                          ]))]),
                          _: 1
                        }, 8, ["block"])
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true),
                (!_unref(isMobile) && _unref(store).tree.length > 0)
                  ? (_openBlock(), _createBlock(_component_v_col, {
                      key: 4,
                      cols: "12",
                      sm: "auto"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_btn, {
                          color: "primary",
                          onClick: _cache[20] || (_cache[20] = ($event: any) => (_unref(store).setAll(!_unref(store).anyExpanded)))
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(_unref(store).anyExpanded ? 'COLLAPSE ALL' : 'EXPAND ALL'), 1)
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true),
                (!_unref(isMobile))
                  ? (_openBlock(), _createBlock(_component_v_spacer, { key: 5 }))
                  : _createCommentVNode("", true),
                (!_unref(isMobile))
                  ? (_openBlock(), _createBlock(_component_v_col, {
                      key: 6,
                      cols: "12",
                      sm: "auto"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_menu, {
                          "offset-y": "",
                          "close-on-content-click": false
                        }, {
                          activator: _withCtx(({ props: menuProps }) => [
                            _createVNode(_component_v_btn, _mergeProps({ icon: "mdi-cog" }, menuProps, { elevation: "0" }), null, 16)
                          ]),
                          default: _withCtx(() => [
                            _createVNode(_component_v_card, { "min-width": "300" }, {
                              default: _withCtx(() => [
                                _createVNode(_component_v_card_text, null, {
                                  default: _withCtx(() => [
                                    _createVNode(_component_v_checkbox, {
                                      modelValue: simplify.value,
                                      "onUpdate:modelValue": _cache[21] || (_cache[21] = ($event: any) => ((simplify).value = $event)),
                                      label: "Simplified View"
                                    }, null, 8, ["modelValue"]),
                                    _createVNode(_component_v_text_field, {
                                      modelValue: backendUrl.value,
                                      "onUpdate:modelValue": _cache[22] || (_cache[22] = ($event: any) => ((backendUrl).value = $event)),
                                      label: "Backend URL",
                                      hint: "e.g. http://localhost:21999/",
                                      "persistent-hint": ""
                                    }, null, 8, ["modelValue"])
                                  ]),
                                  _: 1
                                })
                              ]),
                              _: 1
                            })
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            }))
          : _createCommentVNode("", true),
        (_unref(store).tabs.length > 0)
          ? (_openBlock(), _createBlock(_component_v_row, {
              key: 1,
              id: "tab-content"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_v_col, {
                  id: "tabs",
                  cols: "12",
                  md: "2"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_tabs, {
                      modelValue: currentTab.value,
                      "onUpdate:modelValue": _cache[24] || (_cache[24] = ($event: any) => ((currentTab).value = $event)),
                      direction: _unref(isMobile) ? 'horizontal' : 'vertical',
                      "show-arrows": ""
                    }, {
                      default: _withCtx(() => [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(store).tabs, (tab) => {
                          return (_openBlock(), _createBlock(_component_v_tab, {
                            key: tab.id,
                            value: tab.id
                          }, {
                            default: _withCtx(() => [
                              _createElementVNode("div", _hoisted_3, [
                                _createElementVNode("div", _hoisted_4, _toDisplayString(tab.name), 1),
                                _createVNode(_component_v_btn, {
                                  elevation: "0",
                                  icon: "mdi-close",
                                  size: "x-small",
                                  onClick: _withModifiers(($event: any) => (_unref(store).tabRemoved(tab.id)), ["stop"])
                                }, null, 8, ["onClick"])
                              ])
                            ]),
                            _: 2
                          }, 1032, ["value"]))
                        }), 128)),
                        _createVNode(_component_v_tab, {
                          onClick: _cache[23] || (_cache[23] = ($event: any) => (_unref(store).addTab('Unnamed'))),
                          style: {"justify-content":"center"}
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_icon, null, {
                              default: _withCtx(() => [...(_cache[54] || (_cache[54] = [
                                _createTextVNode("mdi-plus", -1)
                              ]))]),
                              _: 1
                            })
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }, 8, ["modelValue", "direction"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_v_col, {
                  cols: "12",
                  md: "",
                  class: "center-col"
                }, {
                  default: _withCtx(() => [
                    _createElementVNode("div", {
                      style: _normalizeStyle({ position: 'absolute', left: `${menuX.value}px`, top: `${menuY.value}px` }),
                      ref_key: "activatorRef",
                      ref: activatorRef
                    }, [
                      _createVNode(MenuComponent, { items: contextItems.value }, null, 8, ["items"])
                    ], 4),
                    _createElementVNode("div", _hoisted_5, [
                      (_unref(store).tree.length > 0)
                        ? (_openBlock(), _createBlock(TreeNodeComponent, {
                            key: 0,
                            node: findRoot(),
                            onRightclick: _cache[25] || (_cache[25] = (x, y, id) => openMenu(x, y, id)),
                            simplify: simplify.value || _unref(isMobile)
                          }, null, 8, ["node", "simplify"]))
                        : (_openBlock(), _createElementBlock("p", _hoisted_6, " No ASN.1 data loaded. Please upload a file or select an example. "))
                    ])
                  ]),
                  _: 1
                }),
                _createVNode(_component_v_col, {
                  cols: "12",
                  md: "4",
                  class: "right-col"
                }, {
                  default: _withCtx(() => [
                    _createElementVNode("div", {
                      class: "byte-grid-container",
                      ref_key: "bytesRef",
                      ref: bytesRef
                    }, [
                      _createElementVNode("div", _hoisted_7, [
                        _createVNode(_component_v_btn, {
                          icon: "mdi-content-copy",
                          size: "small",
                          class: "copy-btn",
                          onClick: copyDER
                        })
                      ]),
                      (_unref(store).tree.length > 0)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(flatBytes.value, (byte, index) => {
                              return (_openBlock(), _createElementBlock("span", {
                                key: index,
                                class: _normalizeClass([
                byte.type,
                highlightedNodeAndDescendants.value.has(byte.nodeId) ? 'highlighted' : ''
              ]),
                                "data-node-id": byte.nodeId,
                                onClick: ($event: any) => (_unref(store).elementHighlighted(byte.nodeId))
                              }, _toDisplayString(dec2hex(byte.value)), 11, _hoisted_9))
                            }), 128))
                          ]))
                        : _createCommentVNode("", true)
                    ], 512)
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }))
          : _createCommentVNode("", true),
        (_unref(store).tabs.length === 0)
          ? (_openBlock(), _createBlock(_component_v_row, { key: 2 }, {
              default: _withCtx(() => [
                _createVNode(UploadCard, { onReportLoaded: handleReportLoaded })
              ]),
              _: 1
            }))
          : _createCommentVNode("", true)
      ]),
      _: 1
    })
  ], 64))
}
}

})